'use strict'
const forEach_ = require('lodash/forEach')
const includes_ = require('lodash/includes')
const unescape_ = require('lodash/unescape')
const maybe = require('folktale/maybe')
const result = require('folktale/result')

const { selectCurrentRecord } = require('../dataset-controller/rootReducer')
const isInputComponent = require('dbsm-common/src/isInputComponent')
const convertValueToString = require('../helpers/convertValueToString')
const getFieldValue = require('../helpers/getFieldValue')
const values_ = require('lodash/values')
const isEmpty_ = require('lodash/isEmpty')
const get_ = require('lodash/get')
const set_ = require('lodash/set')
const { FIELD_TYPES } = require('dbsm-common/src/collections/consts')
const addComponentEventListener = require('./addComponentEventListener')
const {
  selectNextDynamicPageUrl,
  selectPreviousDynamicPageUrl
} = require('../dataset-controller/rootReducer')
const {
  isFieldFromReferencedCollection,
  getReferenceFieldName
} = require('dbsm-common/src/reference-fields/fieldPath')

module.exports = ({
  getState,
  datasetApi,
  wixSdk,
  errorReporter,
  platformUtilities,
  eventListeners: { register },
  getFieldType,
  errorBoundaries
}) => {
  const setFieldOnComponentChange = (component, properties, actions) => {
    const getValue = (fieldName, value) => {
      if (getFieldType(fieldName) === 'reference') {
        return result.try(() => JSON.parse(value)).matchWith({
          Error: () => value,
          Ok: ({ value: { id, field } }) => {
            const referencedRecord = actions.fetchRecordById(id, field)
            return referencedRecord.getOrElse(id)
          }
        })
      }
      return value
    }

    addComponentEventListener(
      component,
      'onChange',
      event => {
        const propName = properties.checked ? 'checked' : 'value'
        const fieldName = properties[propName].fieldName
        const value = getValue(fieldName, event.target[propName])

        actions.setFieldInCurrentRecordAndSynchronize(
          fieldName,
          value,
          component.uniqueId
        )
      },
      errorBoundaries
    )
  }

  const getNavigateUrl = (navigate, record) => {
    if (navigate.fieldName) {
      return record[navigate.fieldName]
    }

    if (navigate.linkObject) {
      return platformUtilities.links.toUrl(navigate.linkObject)
    }
  }

  const navigateToDynamicPage = dynamicPageUrlState =>
    dynamicPageUrlState.matchWith({
      Empty() {},
      Loading() {},
      Loaded({ url }) {
        wixSdk.location.to(url)
      }
    })

  const dispatchActionOnComponentEvent = (
    component,
    eventName,
    action,
    postAction
  ) => {
    component[eventName](async function() {
      try {
        if (action === 'nextDynamicPage') {
          navigateToDynamicPage(selectNextDynamicPageUrl(getState()))
          return
        }
        if (action === 'previousDynamicPage') {
          navigateToDynamicPage(selectPreviousDynamicPageUrl(getState()))
          return
        }
        const record = await Promise.resolve(datasetApi[action]())
        if (postAction && postAction.navigate) {
          const url = getNavigateUrl(postAction.navigate, record)
          wixSdk.location.to(url)
        }
      } catch (e) {
        errorReporter(`${action} operation failed:`, e)
      }
    })
  }

  const setValueToComponent = (component, propPath, newValue, fieldType) => {
    const oldValue = get_(component, propPath)

    if (isInputComponent(component)) {
      set_(component, propPath, newValue)
      //set_ doesn't work if oldValue and newValue are the same.
      //Input components have setter for value(checked, etc) which updates validityIndictor.
      //this is why we update it manually here.
      if (oldValue === newValue) {
        component.updateValidityIndication()
      }
      return
    }
    set_(component, propPath, convertValueToString(newValue))
    if (fieldType === FIELD_TYPES.URL) {
      component.text = `<a href=${
        component.text
      } target="_blank" style="text-decoration: underline">${
        component.text
      }</a>`
      const compHTML = component.html
      component.text = ''
      component.html = unescape_(compHTML)
      return
    }
  }

  const updateComponentFromCurrentRecord = (
    { connectionConfig: { properties }, component },
    actions,
    updatedFields = []
  ) => {
    const data = maybe.fromNullable(selectCurrentRecord(getState()))
    data.matchWith({
      Nothing: () => {},
      Just: ({ value: record }) => {
        forEach_(properties, ({ fieldName }, propPath) => {
          try {
            if (
              updatedFields.length === 0 ||
              includes_(
                updatedFields,
                isFieldFromReferencedCollection(fieldName)
                  ? getReferenceFieldName(fieldName)
                  : fieldName
              )
            ) {
              setValueToComponent(
                component,
                propPath,
                getFieldValue(record, fieldName),
                getFieldType(fieldName)
              )
            }
          } catch (e) {
            errorReporter(`Failed setting ${propPath}:`, e)
          }
        })
      }
    })
  }

  const syncValidityIndication = ({ component }, actions) => {
    const pristine = actions.isCurrentRecordPristine(getState())
    const newRecord = actions.isCurrentRecordNew(getState())
    const inputComponent = isInputComponent(component)
    if (pristine && newRecord && inputComponent) {
      component.resetValidityIndication()
    }
  }

  const sync = actions => () => {
    actions.refresh()
  }

  const applyBehaviorToComponent = (behavior, component) => {
    let ignoreNextIndexChange = false
    switch (behavior) {
      case 'saveSuccessFeedback':
        register('beforeSave', () => component.hide())
        register('afterSave', () => {
          component.show()
          ignoreNextIndexChange = true
        })
        register('currentIndexChanged', () => {
          if (ignoreNextIndexChange) {
            ignoreNextIndexChange = false
          } else {
            component.hide()
          }
        })
        register('itemValuesChanged', () => component.hide())
        break
      case 'saveFailureFeedback':
        register('beforeSave', () => component.hide())
        register('currentIndexChanged', () => component.hide())
        register('datasetError', operationName => {
          if (operationName === 'save') {
            component.show()
          }
        })
        break
    }
  }

  const isBoundToInputProperty = properties =>
    get_(properties, 'value') || get_(properties, 'checked')

  return {
    isValidContext({ connectionConfig }) {
      return values_(connectionConfig).find(
        configValue => !isEmpty_(configValue)
      )
    },

    bindToComponent({ connectionConfig, component }, actions) {
      const { properties, events, filters, behaviors } = connectionConfig

      if (isBoundToInputProperty(properties)) {
        setFieldOnComponentChange(component, properties, actions)
      }

      if (events) {
        forEach_(events, ({ action, postAction }, eventName) => {
          dispatchActionOnComponentEvent(
            component,
            eventName,
            action,
            postAction
          )
        })
      }

      if (filters) {
        if (typeof component.onChange === 'function') {
          addComponentEventListener(
            component,
            'onChange',
            sync(actions),
            errorBoundaries
          )
        }
      }

      if (behaviors) {
        forEach_(behaviors, ({ type: behavior }) => {
          applyBehaviorToComponent(behavior, component)
        })
      }
    },

    currentRecordModified(componentAdapterContext, actions, updatedFields) {
      updateComponentFromCurrentRecord(
        componentAdapterContext,
        actions,
        updatedFields
      )
      syncValidityIndication(componentAdapterContext, actions)
    },

    recordSetLoaded(componentAdapterContext, actions) {
      updateComponentFromCurrentRecord(componentAdapterContext, actions)
      syncValidityIndication(componentAdapterContext, actions)

      const { behaviors } = componentAdapterContext.connectionConfig
      if (behaviors) {
        forEach_(behaviors, ({ type: behavior }) => {
          if (behavior === 'saveSuccessFeedback') {
            componentAdapterContext.component.hide()
          }
        })
      }
    },

    currentViewChanged(componentAdapterContext, actions) {
      updateComponentFromCurrentRecord(componentAdapterContext, actions)
      syncValidityIndication(componentAdapterContext, actions)
    },

    currentIndexChanged(componentAdapterContext, actions) {
      updateComponentFromCurrentRecord(componentAdapterContext, actions)
      syncValidityIndication(componentAdapterContext, actions)
    }
  }
}



// WEBPACK FOOTER //
// ./adapters/defaultAdapter.js